import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CockpitDataProvider } from "./pages/cockpit/CockpitDataContext";
import CrmComptes from "./pages/crm/CrmComptes";
import CrmPerimetre from "./pages/crm/CrmPerimetre";
import CrmCartographie from "./pages/crm/CrmCartographie";
import CrmFiche from "./pages/crm/CrmFiche";
import Reporting from "./pages/crm/Reporting";
import { Toaster } from "./components/ui/toaster";
import ErrorBoundary from "./components/ErrorBoundary";
import RouteTracker from "./components/RouteTracker";
import ClickTracker from "./components/ClickTracker";
import BugReportRoot from "./components/BugReportRoot";

// Pages
import LandingPage from "./pages/LandingPage";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Cockpit from "./pages/Cockpit";
import Library from "./pages/Library";
import NewDossier from "./pages/NewDossier";
import DossierForm from "./pages/DossierForm";
import Tenders from "./pages/Tenders";
import NewTender from "./pages/NewTender";
import TenderDetail from "./pages/TenderDetail";
import Shortlists from "./pages/Shortlists";
import DossierEditor from "./pages/DossierEditor";
import Billing from "./pages/Billing";
import Members from "./pages/Members";
import Settings from "./pages/Settings";
import SentEmails from "./pages/SentEmails";
import AcceptInvite from "./pages/AcceptInvite";
import AdminOverview from "./pages/AdminOverview";
import AdminOrgs from "./pages/AdminOrgs";
import AdminOrgDetail from "./pages/AdminOrgDetail";
import AdminUsers from "./pages/AdminUsers";
import AdminPayments from "./pages/AdminPayments";
import AdminActivity from "./pages/AdminActivity";
import AdminBugReports from "./pages/AdminBugReports";
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/" />;
};

// Admin Route — requires platform admin flag
const AdminRoute = ({ children }) => {
  const { user, isPlatformAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/" />;
  if (!isPlatformAdmin) return <Navigate to="/dashboard" />;
  return children;
};

// Public Route Component (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }
  
  return !user ? children : <Navigate to="/dashboard" />;
};

function App() {
  return (
    <AuthProvider>
      <CockpitDataProvider>
      <BrowserRouter>
        {/* Telemetry (renderless) — captures route changes and clicks into
            the in-memory breadcrumb buffer used by bug reports. */}
        <RouteTracker />
        <ClickTracker />
        <ErrorBoundary>
        <Routes>
          {/* Landing page — public, but logged-in users go to dashboard */}
          <Route
            path="/"
            element={
              <PublicRoute>
                <LandingPage />
              </PublicRoute>
            }
          />

          {/* Public Routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Navigate to="/" state={{ openLogin: true }} replace />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          {/* Cockpit BM (bêta) — /cockpit/comptes/:account doit précéder /cockpit/:section */}
          <Route
            path="/cockpit"
            element={
              <ProtectedRoute>
                <Cockpit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cockpit/comptes/:account"
            element={
              <ProtectedRoute>
                <Cockpit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cockpit/:section"
            element={
              <ProtectedRoute>
                <Cockpit />
              </ProtectedRoute>
            }
          />
          {/* CRM · le client — /crm/comptes/:account ouvre la fiche d'un compte */}
          <Route
            path="/crm/comptes"
            element={
              <ProtectedRoute>
                <CrmComptes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/comptes/:account"
            element={
              <ProtectedRoute>
                <CrmComptes />
              </ProtectedRoute>
            }
          />
          {/* Périmètre — /crm/perimetre/:account cible un compte précis */}
          <Route
            path="/crm/perimetre"
            element={
              <ProtectedRoute>
                <CrmPerimetre />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/perimetre/:account"
            element={
              <ProtectedRoute>
                <CrmPerimetre />
              </ProtectedRoute>
            }
          />
          {/* Cartographie — organigramme d'un compte, /crm/cartographie/:account cible un compte précis */}
          <Route
            path="/crm/cartographie"
            element={
              <ProtectedRoute>
                <CrmCartographie />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/cartographie/:account"
            element={
              <ProtectedRoute>
                <CrmCartographie />
              </ProtectedRoute>
            }
          />
          {/* Reporting — vue globale, tous comptes confondus */}
          <Route
            path="/reporting"
            element={
              <ProtectedRoute>
                <Reporting />
              </ProtectedRoute>
            }
          />
          {/* Fiche client — /crm/fiches/new?account=&entity=&team= pour une création rattachée */}
          <Route
            path="/crm/fiches/:id"
            element={
              <ProtectedRoute>
                <CrmFiche />
              </ProtectedRoute>
            }
          />
          <Route
            path="/library"
            element={
              <ProtectedRoute>
                <Library />
              </ProtectedRoute>
            }
          />
          {/* IMPORTANT: /dossier/new must come BEFORE /dossier/:id so the
              streamlined NewDossier page captures it before DossierForm. */}
          <Route
            path="/dossier/new"
            element={
              <ProtectedRoute>
                <NewDossier />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dossier/:id"
            element={
              <ProtectedRoute>
                <DossierForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dossier/edit/:id"
            element={
              <ProtectedRoute>
                <DossierForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dossiers/:id/edit"
            element={
              <ProtectedRoute>
                <DossierEditor />
              </ProtectedRoute>
            }
          />
          {/* Tenders / Appels d'offre — /new must come before /:id */}
          <Route
            path="/tenders"
            element={
              <ProtectedRoute>
                <Tenders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tenders/new"
            element={
              <ProtectedRoute>
                <NewTender />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tenders/:id"
            element={
              <ProtectedRoute>
                <TenderDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/shortlists"
            element={
              <ProtectedRoute>
                <Shortlists />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute>
                <Billing />
              </ProtectedRoute>
            }
          />
          <Route
            path="/members"
            element={
              <ProtectedRoute>
                <Members />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/emails"
            element={
              <ProtectedRoute>
                <SentEmails />
              </ProtectedRoute>
            }
          />

          {/* Password reset flow — public, no PublicRoute wrapper (recovery session must remain active) */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Invite accept — accessible even without login (handles auth internally) */}
          <Route path="/accept-invite" element={<AcceptInvite />} />

          {/* Admin Routes */}
          <Route path="/admin"           element={<AdminRoute><AdminOverview  /></AdminRoute>} />
          <Route path="/admin/orgs"      element={<AdminRoute><AdminOrgs      /></AdminRoute>} />
          <Route path="/admin/orgs/:orgId" element={<AdminRoute><AdminOrgDetail /></AdminRoute>} />
          <Route path="/admin/users"     element={<AdminRoute><AdminUsers     /></AdminRoute>} />
          <Route path="/admin/payments"  element={<AdminRoute><AdminPayments  /></AdminRoute>} />
          <Route path="/admin/activity"  element={<AdminRoute><AdminActivity  /></AdminRoute>} />
          <Route path="/admin/bug-reports" element={<AdminRoute><AdminBugReports /></AdminRoute>} />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        </ErrorBoundary>
        {/* Floating bug-report button + modal — gates on auth internally. */}
        <BugReportRoot />
        <Toaster />
      </BrowserRouter>
      </CockpitDataProvider>
    </AuthProvider>
  );
}

export default App;
